<template>
    <v-card>
        <v-card-title class="text-h6" primary-title>
            {{ embedded ? "Samples Collection" : "Create Material Samples" }}
            <v-spacer></v-spacer>
        </v-card-title>
        <v-card-text>
            <v-form v-model="localValid" validate-on="input">
                <v-row>
                    <v-col cols="12">
                        <v-autocomplete
                            v-model="localModel.collectionSOP"
                            :items="sopOptions"
                            item-title="name"
                            return-object
                            label="Sample Collection SOP"
                            required
                            :rules="[(value) => !!value || 'Sample Collection SOP is required']"
                            @update:model-value="handleSOPChange"
                        ></v-autocomplete>
                    </v-col>
                </v-row>
                <v-row v-if="localModel.collectionSOP">
                    <v-col cols="6">
                        <v-autocomplete
                            label="Material type"
                            v-model="localModel.materialType"
                            :items="materialTypeOptions"
                            item-title="name"
                            return-object
                            required
                            :rules="[
                                (value) =>
                                    (!!value && !!localModel.materialType) ||
                                    'Material type is required',
                            ]"
                        ></v-autocomplete>
                    </v-col>
                    <v-col cols="6">
                        <v-text-field
                            label="# samples/source"
                            type="number"
                            v-model="localModel.samplesPerSource"
                            :rules="[
                                (value) =>
                                    (!!value && !!localModel.samplesPerSource) ||
                                    '# samples/source is required',
                            ]"
                            required
                        ></v-text-field>
                    </v-col>
                </v-row>
                <v-row v-if="localModel.collectionSOP">
                    <v-col cols="12">
                        <v-autocomplete
                            label="Sample descriptors"
                            v-model="localModel.descriptors"
                            :items="descriptorOptions"
                            item-title="name"
                            return-object
                            multiple
                            chips
                            clearable
                            deletable-chips
                            :rules="[
                                (value) =>
                                    (value && value.length == localModel.samplesPerSource) ||
                                    'Number of descriptors must match # samples/source',
                            ]"
                            required
                        ></v-autocomplete>
                    </v-col>
                </v-row>
            </v-form>
        </v-card-text>
        <v-card-actions v-if="!embedded">
            <v-spacer></v-spacer>
            <v-btn @click="cancel" color="primary">Cancel</v-btn>
            <v-btn @click="handleSave" color="primary" :disabled="!localValid">Save Samples</v-btn>
        </v-card-actions>
    </v-card>
</template>

<script setup lang="ts">
import { ref, watch, toRefs, computed, onMounted } from "vue";
import { Experiment } from "@/services/things/models";
import {
    MaterialSamplesContext,
    MaterialSampleSOP,
    MaterialType,
    SampleDescriptor,
    samplesAPI,
    SavedMaterialSampleSet,
    SavedMaterialSampleSOP,
    SaveMaterialSampleSetParams,
    UnsavedMaterialSample,
} from "@/services/samples/samplesAPI";
import { withLoadingScreen } from "@/lib/loading";

const props = defineProps<{
    modelValue: MaterialSamplesContext;
    experiment: Experiment;
    things: Array<{
        id: string;
        name: string;
    }>;
    sops: SavedMaterialSampleSOP[];
    valid: boolean;
    embedded: boolean;
}>();

const emit = defineEmits<{
    (e: "update:modelValue", value: MaterialSamplesContext): void;
    (e: "update:valid", value: boolean): void;
    (e: "canceled"): void;
    (e: "saved", value: SavedMaterialSampleSet): void;
}>();

onMounted(() => {
    fetchMaterialTypeOptions();
    fetchDescriptorOptions();
})

const { modelValue } = toRefs(props);
const localModel = ref({ ...modelValue.value });
const localValid = ref(props.valid);
const materialTypeOptions = ref<MaterialType[]>([]);
const descriptorOptions = ref<SampleDescriptor[]>([]);

const sopOptions = computed(() => {
    return [
        ...props.sops,
        { id: null, name: "<No SOP: configure manually>" } as unknown as MaterialSampleSOP,
    ];
});

watch(
    localModel,
    (val) => {
        if (localModel.value.things?.length === 0) {
            localModel.value.things = props.things.map((thing) => ({
                id: thing.id,
                name: thing.name,
            }));
        }

        emit("update:modelValue", val);
    },
    { deep: true }
);
watch(localValid, (val) => emit("update:valid", !!val));
watch(modelValue, (val) => {
    // keep in sync if parent changes modelValue; this is useful for resetting the form
    if (val && val !== localModel.value) localModel.value = structuredClone(val);
});

function cancel() {
    localModel.value.collectionSOP = null;
    localModel.value.materialType = null;
    localModel.value.samplesPerSource = null;
    localModel.value.descriptors = [];
    localModel.value.needByDate = null;
    localModel.value.things = [];
    localValid.value = false;
    emit("canceled");
}

async function handleSave() {
    await withLoadingScreen(async () => {
        const savedSampleSet = await saveSampleSet();
        emit("saved", savedSampleSet);
    });
}

async function saveSampleSet() {
    const samplesContext = localModel.value;

    const samples: UnsavedMaterialSample[] = [];
    for (const thing of samplesContext.things!) {
        for (const descriptor of samplesContext.descriptors) {
            samples.push({
                type: samplesContext.materialType!.id,
                thingInstances: [thing],
                descriptor: descriptor.id,
                experimentCode: samplesContext.experimentCode,
                collectionDate: null,
                thingInstanceIds: [thing.id],
            });
        }
    }
    const payload: SaveMaterialSampleSetParams = {
        sampleSopId: samplesContext.collectionSOP!["id"] || undefined,
        needByDate: null,
        samples,
        sampleDescriptorIds: samplesContext.descriptors.map((descriptor) => descriptor.id),
        materialTypeId: samplesContext.materialType!.id,
    };

    return samplesAPI.createSampleSet(samplesContext.experimentCode!, payload);
}

async function fetchMaterialTypeOptions() {
    await withLoadingScreen(async () => {
        materialTypeOptions.value = await samplesAPI.fetchMaterialTypes();
    });
}

async function fetchDescriptorOptions() {
    await withLoadingScreen(async () => {
        descriptorOptions.value = await samplesAPI.fetchSampleDescriptors();
    });
}

function handleSOPChange(sop: MaterialSampleSOP) {
    if (sop && sop["id"] !== null) {
        // existing SOP selected, populate material type and descriptors from SOP
        const existingSOP = props.sops.find((s) => s.id === sop["id"]);
        if (existingSOP) {
            localModel.value.materialType = materialTypeOptions.value.find(
                (mt) => mt.id === existingSOP.materialTypeId
            ) || null;
            localModel.value.descriptors = descriptorOptions.value.filter((desc) =>
                existingSOP.sampleDescriptorIds.includes(desc.id)
            );
            localModel.value.samplesPerSource = existingSOP.sampleDescriptorIds.length;
        }
    } else {
        // manual configuration selected, clear material type and descriptors
        localModel.value.materialType = null;
        localModel.value.descriptors = [];
        localModel.value.samplesPerSource = null;
    }
}

defineExpose({
    saveSampleSet,
});
</script>
